import chroma from 'chroma-js';
import React, { ChangeEventHandler, HTMLAttributes, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as Icon } from '../../../../img/checkbox.svg';
import { ReactComponent as IconChecked } from '../../../../img/checkbox_checked.svg';
import { ReactComponent as IconPartlyChecked } from '../../../../img/checkbox_partly_checked.svg';
import { theme } from '../../../../theme';
import { Caption } from '../typography';
import Body01 from '../typography/Body01';

export enum CheckboxState {
    Checked = 'checked',
    PartlyChecked = 'partlyChecked',
    NotChecked = 'notChecked'
}

export interface CheckboxProps extends Omit<HTMLAttributes<HTMLInputElement>, 'onClick'> {
    assistiveText?: string;
    background?: string;
    backgroundChecked?: string;
    /** 
     * @deprecated Use `state` property instead
     */
    checked?: boolean;
    className?: string;
    disabled?: boolean;
    /**
     * Element to display as a label for checkbox.
     * 
     * Please don't use block elements for label as it will be rendered inside `label` tag
     * and this isn't allowed by HTML specification.
     */
    label?: React.ReactNode;
    /**
     * @deprecated
     * Use onChange handler instead. If you need to determine target use native `name` property.
     */
    onClick?: (label?: React.ReactNode, event?: React.ChangeEvent) => void;
    state?: CheckboxState;
    withEllipsis?: boolean;
    withLineClimb?: boolean;
    maxLines?: number;
}

const Checkbox: React.FC<CheckboxProps> = ({
    assistiveText,
    background = theme.semanticColor.highlightSecondary,
    backgroundChecked = theme.semanticColor.highlightPrimary,
    className = '',
    checked,
    disabled = false,
    label,
    onChange,
    onClick = () => null,
    state = CheckboxState.NotChecked,
    withEllipsis = false,
    withLineClimb = false,
    maxLines = 3,
    ...restProps
}): React.ReactElement => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (!disabled) {
            onClick(label, event);
        }

        if (onChange) {
            onChange(event);
        }
    };

    const checkboxState = useMemo(() => {
        if (typeof checked !== 'undefined') {
            return checked ? CheckboxState.Checked : CheckboxState.NotChecked;
        }

        return state;
    }, [ state, checked ]);

    useEffect(() => {
        if (checkboxState !== CheckboxState.PartlyChecked || !inputRef.current) {
            return;
        }

        // The only way to set indeterminate state to checkbox
        inputRef.current.indeterminate = true;
    }, [ checkboxState ]);

    const hasTextBlock = Boolean(assistiveText || label);

    return (
        <Label className={ className }
            disabled={ disabled }
            withEllipsis={ withEllipsis }
        >
            <InvisibleCheckbox { ...restProps }
                checked={ checkboxState === CheckboxState.Checked }
                disabled={ disabled }
                background={ background }
                backgroundChecked={ backgroundChecked }
                ref={ inputRef }
                onChange={ handleChange }
            />

            <PseudoCheckbox>
                { checkboxState === CheckboxState.NotChecked && <StyledIcon /> }

                { checkboxState === CheckboxState.Checked && <StyledIconChecked /> }

                { checkboxState === CheckboxState.PartlyChecked && <StyledIconPartlyChecked /> }
            </PseudoCheckbox>

            { hasTextBlock && (
                <TextContainer>
                    { label && (
                        <Text withEllipsis={ withEllipsis }
                            withLineClimb={ withLineClimb }
                            maxLines={ maxLines }
                        >
                            { label }
                        </Text>
                    ) }
                    { assistiveText && (
                        <Caption as="span">
                            { assistiveText }
                        </Caption>
                    ) }
                </TextContainer>
            ) }
        </Label>
    );
};

export default styled(Checkbox)``;

const dimensionStyle = css`
    height: ${ ({ theme }) => theme.size.size_40 };
    width: ${ ({ theme }) => theme.size.size_40 };
`;

export const TextContainer = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    user-select: none;
    margin-left: ${ ({ theme }) => theme.size.size_2 };
    flex: 1;
`;

export const Text = styled(Body01).attrs({
    as: 'span'
})<{
    withLineClimb?: boolean;
    withEllipsis?: boolean;
    maxLines: number;
}>`
    display: block;
    color: ${ ({ theme }) => theme.semanticColor.textPrimary };
    width: 100%;

    ${ ({ withLineClimb, maxLines }) => withLineClimb && css`
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${ maxLines };
        display: -webkit-box;
        overflow: hidden;
    ` }

    ${ ({ withEllipsis }) => withEllipsis && css`
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    ` }
`;

const StyledIconChecked = styled(IconChecked)`
    path {
        fill: ${ ({ theme }) => theme.semanticColor.primary };
    }
`;

const StyledIconPartlyChecked = styled(IconPartlyChecked)`
    path {
        fill: ${ ({ theme }) => theme.semanticColor.primary };
    }
`;

const StyledIcon = styled(Icon)`
    path {
        fill: ${ ({ theme }) => theme.semanticColor.icon_01 };
    }
`;

const Label = styled.label<{ disabled?: boolean; withEllipsis?: boolean }>`
    align-items: flex-start;
    cursor: pointer;
    display: flex;
    position: relative;

    ${ ({ withEllipsis, theme }) => withEllipsis && `min-width: ${ theme.size.size_40 };` }

    ${ ({ disabled }) => disabled && css`
        cursor: not-allowed;

        ${ Text },
        ${ Caption } {
            cursor: not-allowed;
            color: ${ ({ theme }) => theme.semanticColor.textDisabled };
        }

        ${ StyledIconChecked },
        ${ StyledIcon },
        ${ StyledIconPartlyChecked } {
            path {
                fill: ${ ({ theme }) => theme.semanticColor.textDisabled };
            }
        }
    ` }
`;

export const PseudoCheckbox = styled.span`
    display: block;
    background-color: transparent;
    border-radius: 50%;
    flex-shrink: 0;
    padding: calc(${ ({ theme }) => theme.size.size_10 } - ${ ({ theme }) => theme.size.size_2 });
    position: relative;
    transition: all 150ms;
    z-index: 0;
    margin: ${ ({ theme }) => `-${ theme.size.size_5 } 0` };

    ${ dimensionStyle }

    svg {
        max-width: 100%;
        max-height: 100%;
    }
`;

export const InvisibleCheckbox = styled.input.attrs({ type: 'checkbox' })<{
    background: string;
    backgroundChecked: string;
}>`
    ${ dimensionStyle }

    position: absolute;
    left: 0;
    top: -${ ({ theme }) => theme.size.size_5 };
    z-index: 1;
    opacity: 0;
    border: none;
    margin: 0;
    cursor: pointer;

    &:not(:disabled) {
        &:focus-visible + ${ PseudoCheckbox } {
            outline: none;
            background-color: transparent;
            box-shadow: ${ ({ theme }) => `0px 0px 0px 2px ${ theme.semanticColor.focus }` };
        }

        &:hover + ${ PseudoCheckbox } {
            background-color: ${ ({ background }) => background };
        }

        &:checked:hover + ${ PseudoCheckbox },
        &:indeterminate:hover + ${ PseudoCheckbox } {
            background-color: ${ ({ backgroundChecked }) => backgroundChecked };
        }

        @media (hover: none) {
            &:hover + ${ PseudoCheckbox } {
                background: transparent;
            }
        }


        &:active + ${ PseudoCheckbox } {
            background-color: ${ ({ theme }) => theme.semanticColor.hover_row };
        }

        &:checked:active + ${ PseudoCheckbox },
        &:indeterminate:active + ${ PseudoCheckbox } {
            background-color: ${ ({ theme }) => chroma(theme.semanticColor.primary).alpha(0.1).css() };
        }
    }
`;
